import type { TrackingContextValue } from '../../providers/TrackingProvider';

/**
 * Materialise the per-step `StepCorrelation` carried on a
 * {@link TrackingContextValue} as `customDimensions` entries so every
 * in-flow event payload built downstream lands `stepId / stepIndex /
 * stepType / dslConfigId / pageCategory` on the wire.
 *
 * Empty / `undefined` fields are skipped. The existing
 * `eventContext.customDimensions` array (carrying
 * `sourceCorrelationId`, UTM params, etc.) is
 * preserved by appending — `normalizeCustomDimensions` deduplicates
 * downstream.
 *
 * Extracted from `Trackable.tsx` so both the visibility-driven
 * `Trackable` HOC and the formKey-capture-driven `createBoundRender`
 * (FieldInputted firing — bug fix companion) can re-use the same
 * override shape without circular imports. Pure function, no React
 * hooks — safe to call from any render scope.
 */
export function buildStepCorrelationOverride(tracking: TrackingContextValue): {
  customDimensions: Array<{ key: string; value: string }>;
  dslConfigId?: string;
  dslConfigVersion?: number;
} {
  const { stepCorrelation, eventContext } = tracking;
  const inherited = eventContext?.customDimensions ?? [];
  const additions: Array<{ key: string; value: string }> = [];
  if (stepCorrelation.stepId) {
    additions.push({ key: 'stepId', value: stepCorrelation.stepId });
  }
  if (typeof stepCorrelation.stepIndex === 'number') {
    additions.push({
      key: 'stepIndex',
      value: String(stepCorrelation.stepIndex),
    });
  }
  if (stepCorrelation.stepType) {
    additions.push({ key: 'stepType', value: stepCorrelation.stepType });
  }
  if (stepCorrelation.dslConfigId) {
    additions.push({
      key: 'dslConfigId',
      value: stepCorrelation.dslConfigId,
    });
  }
  if (stepCorrelation.pageCategory) {
    additions.push({
      key: 'pageCategory',
      value: stepCorrelation.pageCategory,
    });
  }
  return {
    customDimensions: [...inherited, ...additions],
    ...(stepCorrelation.dslConfigId
      ? { dslConfigId: stepCorrelation.dslConfigId }
      : {}),
    ...(stepCorrelation.dslConfigVersion !== undefined
      ? { dslConfigVersion: stepCorrelation.dslConfigVersion }
      : {}),
  };
}
